/**
 * Eid al-Adha coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidAlAdhaColoringPagesPack: ToilePack = {
    slug: 'eid-al-adha-coloring-pages',
    category: 'season',
    name: 'Eid al-Adha coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'ram', label: 'Ram', subject: 'a ram with curled horns and a woolly coat standing in a meadow with a flower garland round its neck, a crescent moon above'},
        {key: 'mosque', label: 'Mosque', subject: 'a mosque with a large dome and two minarets under a crescent moon and stars, "Eid Mubarak" lettered in outline across the sky'},
        {key: 'lanterns', label: 'Lanterns', subject: 'three hanging lanterns with pierced geometric panels and a crescent finial, strings of stars between them'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and easy', style: 'thick black outlines of even weight, large simple shapes and wide open spaces for small hands and crayons', palette: 'black ink only', framing: 'The drawing centered on the page with a wide white margin'},
        {key: 'geometric', label: 'Geometric fill', style: 'medium black outlines with the shapes filled with Islamic geometric star patterns and arabesque leaves to color slowly', palette: 'black ink only', framing: 'The drawing filling the page with a thin white margin'},
        {key: 'arch', label: 'Arch frame', style: 'clean medium black outlines set like a picture in a doorway', palette: 'black ink only', framing: 'The drawing set inside a pointed mosque-arch border of double lines, a white margin around the arch'},
    ],
    directions: ['a goat', 'a camel', 'Eid card'],
    directionHint: 'try: a goat',
    seo: {
        title: 'Eid al-Adha Coloring Pages: Ram, Mosque, Lanterns',
        metaDescription: 'Printable Eid al-Adha coloring pages: a ram, a mosque at Eid and hanging lanterns, in three line styles.',
        h1: 'Eid al-Adha coloring pages: ram, mosque, lanterns',
        tagline: "Nine outline drawings of the feast's own pictures, in three styles: bold and easy, geometric fill, arch frame.",
        intro: 'Printable coloring pages for Eid al-Adha, for a child to color at home or in a weekend school. Each is a clean outline drawing of a picture from the feast: a ram with curled horns and a flower garland, a mosque with a dome and two minarets under a crescent moon, or three hanging lanterns with pierced panels. Bold and easy, geometric fill or arch frame. Type a different subject, such as a camel, and the same styles carry it.',
        useCases: [
            'a coloring page at home during Eid',
            'a weekend school activity',
            'a classroom craft for Eid al-Adha',
            'a page for a child during the holiday',
        ],
        faq: [
            {q: 'Do the pages include lettering?', a: 'Only where it belongs in the picture: the mosque page has Eid Mubarak across the sky, and the other pages have no words.'},
            {q: 'What is the geometric fill style?', a: 'Medium black outlines with the shapes filled with Islamic geometric star patterns and arabesque leaves, to color slowly.'},
            {q: 'Are the pages in color?', a: 'No, they are black outlines on plain white, made for crayons or markers.'},
        ],
    },
}
